import { FC, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TvIcon from "@mui/icons-material/Tv";
import LaptopIcon from "@mui/icons-material/Laptop";
import { DialogHeader } from "@/shared/components/DialogHeader";
import { useDateFormat, useTranslation } from "@/shared/hooks";
import TakeOverViewSessionMutation from "@/shared/graphql/mutations/TakeOverViewSessionMutation.graphql";
import { getViewerId } from "@/shared/utils/viewer";
import type { TakeOverViewSessionMutationType } from "@/types/graphql/site.generated";
import {
  defaultCloseIds,
  deviceKind,
  type DeviceKind,
  deviceTitle,
  type ViewSessionNode,
} from "./viewSessions";

const DEVICE_ICONS: Record<DeviceKind, typeof LaptopIcon> = {
  phone: SmartphoneIcon,
  tv: TvIcon,
  computer: LaptopIcon,
};

interface ViewSessionsDialogProps {
  open: boolean;
  contentId: string;
  sessions: readonly ViewSessionNode[];
  loading?: boolean;
  onClose: () => void;
  /** Сеанс этого браузера открыт: плеер перезапрашивает доступ. */
  onTakenOver: () => void;
}

/**
 * Где сейчас смотрят (лимит параллельных просмотров): активные сеансы
 * зрителя на контенте и кнопка «Смотреть здесь», которая закрывает
 * выбранные и открывает сеанс этого браузера. По умолчанию выбран самый
 * давний чужой сеанс.
 */
export const ViewSessionsDialog: FC<ViewSessionsDialogProps> = ({
  open,
  contentId,
  sessions,
  loading = false,
  onClose,
  onTakenOver,
}) => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const [selected, setSelected] = useState<string[]>(() =>
    defaultCloseIds(sessions),
  );
  const [failed, setFailed] = useState(false);
  const [takeOver, { loading: saving }] =
    useMutation<TakeOverViewSessionMutationType>(TakeOverViewSessionMutation);

  // Список пришёл или обновился: выбор по умолчанию заново.
  useEffect(() => {
    setSelected(defaultCloseIds(sessions));
  }, [sessions]);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );

  const others = sessions.filter((session) => !session.isCurrentDevice);

  const handleWatchHere = async () => {
    setFailed(false);
    try {
      await takeOver({
        variables: {
          contentId,
          viewerId: getViewerId(),
          closeIds: selected,
        },
      });
      onTakenOver();
    } catch {
      setFailed(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      // Плеер может жить в кликабельной карточке: клики диалога не должны
      // всплывать к ней по дереву React.
      onClick={(event) => event.stopPropagation()}
    >
      <DialogHeader
        title={t("parallelLimit.dialog.title")}
        loading={loading || saving}
        onClose={onClose}
      />
      <DialogContent dividers>
        {others.length > 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("parallelLimit.dialog.hint")}
          </Typography>
        ) : (
          !loading && (
            <Typography variant="body2" color="text.secondary">
              {t("parallelLimit.dialog.empty")}
            </Typography>
          )
        )}
        <List disablePadding>
          {sessions.map((session) => {
            const Icon = DEVICE_ICONS[deviceKind(session.userDeviceType)];
            const title =
              deviceTitle(session) ?? t("parallelLimit.dialog.unknownDevice");
            const secondary = session.isCurrentDevice
              ? t("parallelLimit.dialog.currentDevice")
              : t("parallelLimit.dialog.lastActivity", {
                  date: formatDateTime(String(session.lastSeenAt)),
                });
            if (session.isCurrentDevice) {
              return (
                <ListItem key={session.id}>
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={title} secondary={secondary} />
                </ListItem>
              );
            }
            return (
              <ListItemButton
                key={session.id}
                onClick={() => toggle(session.id)}
                selected={selected.includes(session.id)}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText primary={title} secondary={secondary} />
                <Checkbox
                  edge="end"
                  checked={selected.includes(session.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemButton>
            );
          })}
        </List>
        {failed && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t("parallelLimit.dialog.error")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("parallelLimit.dialog.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => void handleWatchHere()}
          disabled={
            saving || loading || (others.length > 0 && !selected.length)
          }
        >
          {t("parallelLimit.dialog.watchHere")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
